/* ============================================================
   La Nuit — global.js
   Vanilla ES module: utilities + core custom elements.
   Loaded on every page via layout/theme.liquid.

   Per-section scripts access utilities via window.LaNuit.* —
   they don't need to ESM-import this file (avoids Shopify
   asset-URL resolution issues across module files).
   ============================================================ */

// Remove no-js sentinel as soon as JS executes
document.documentElement.classList.remove('no-js');

/* ---------- Utilities ---------- */

export function debounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), wait);
  };
}

export function throttle(fn, limit = 100) {
  let inThrottle;
  return (...args) => {
    if (inThrottle) return;
    fn(...args);
    inThrottle = true;
    setTimeout(() => { inThrottle = false; }, limit);
  };
}

export async function fetchJSON(url, options = {}) {
  const res = await fetch(url, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(options.headers || {})
    },
    ...options
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    const err = new Error(`${res.status} ${res.statusText} — ${text}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export function trapFocus(container, onEscape) {
  const selectors = 'a[href], button:not([disabled]), [tabindex="0"], input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])';
  const focusable = container.querySelectorAll(selectors);
  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  function onKey(e) {
    if (e.key === 'Escape' && onEscape) {
      onEscape();
      return;
    }
    if (e.key !== 'Tab') return;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last?.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first?.focus();
    }
  }

  container.addEventListener('keydown', onKey);
  first?.focus();
  return () => container.removeEventListener('keydown', onKey);
}

export function publishCart(cart) {
  document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart, bubbles: true }));
}

/* ---------- Cart API helpers ---------- */

export const Cart = {
  get: () => fetchJSON('/cart.js'),
  add: (items) => fetchJSON('/cart/add.js', {
    method: 'POST',
    body: JSON.stringify({ items: Array.isArray(items) ? items : [items] })
  }),
  change: (line, quantity) => fetchJSON('/cart/change.js', {
    method: 'POST',
    body: JSON.stringify({ line, quantity })
  }),
  clear: () => fetchJSON('/cart/clear.js', { method: 'POST' })
};

/* ---------- <la-disclosure> — accordion / dropdown toggle ---------- */

class LaDisclosure extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[aria-expanded]');
    this.panel = this.querySelector('[data-disclosure-panel]') || this.querySelector('[hidden]');
    if (!this.button || !this.panel) return;
    // Defensive: sync attribute state on connect. If trigger says collapsed
    // but markup forgot to add [hidden] (or vice-versa), align them now so
    // CSS [hidden] rules take effect from the first paint.
    const expanded = this.button.getAttribute('aria-expanded') === 'true';
    if (expanded) {
      this.panel.removeAttribute('hidden');
    } else {
      this.panel.setAttribute('hidden', '');
    }
    if (this._bound) return;
    this._bound = true;
    this.button.addEventListener('click', () => this.toggle());
  }

  toggle(force) {
    const open = typeof force === 'boolean' ? !force : this.button.getAttribute('aria-expanded') === 'true';
    this.button.setAttribute('aria-expanded', String(!open));
    if (open) {
      this.panel.setAttribute('hidden', '');
    } else {
      this.panel.removeAttribute('hidden');
    }
    this.dispatchEvent(new CustomEvent('disclosure:toggle', { detail: { open: !open }, bubbles: true }));
  }
}
customElements.define('la-disclosure', LaDisclosure);

/* ---------- <quantity-input> — qty stepper for cart / PDP ---------- */

class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="number"]');
    if (!this.input) return;
    this.querySelector('[data-qty-minus]')?.addEventListener('click', () => this.step(-1));
    this.querySelector('[data-qty-plus]')?.addEventListener('click', () => this.step(1));
    this.input.addEventListener('change', () => this.validate());
  }

  step(direction) {
    const min = parseInt(this.input.min || '1', 10);
    const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
    const current = parseInt(this.input.value, 10) || min;
    const next = Math.min(max, Math.max(min, current + direction));
    if (next === current) return;
    this.input.value = next;
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  validate() {
    const min = parseInt(this.input.min || '1', 10);
    const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
    let val = parseInt(this.input.value, 10);
    if (Number.isNaN(val)) val = min;
    val = Math.min(max, Math.max(min, val));
    this.input.value = val;
  }
}
customElements.define('quantity-input', QuantityInput);

/* ---------- <modal-dialog> — minimal dialog with focus trap ---------- */

class ModalDialog extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[role="dialog"]') || this;
    this.querySelectorAll('[data-modal-close]').forEach(el =>
      el.addEventListener('click', () => this.close())
    );
    this.addEventListener('click', (e) => {
      if (e.target === this) this.close();
    });
  }

  open(opener) {
    this.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';
    this._opener = opener || document.activeElement;
    this._release = trapFocus(this.dialog, () => this.close());
    this.dispatchEvent(new CustomEvent('modal:open', { bubbles: true }));
  }

  close() {
    this.setAttribute('hidden', '');
    document.body.style.overflow = '';
    this._release?.();
    this._opener?.focus?.();
    this.dispatchEvent(new CustomEvent('modal:close', { bubbles: true }));
  }
}
customElements.define('modal-dialog', ModalDialog);

/* ---------- Delegated opener: any [data-modal-open="ModalId"] ---------- */

document.addEventListener('click', (e) => {
  const trigger = e.target.closest('[data-modal-open]');
  if (!trigger) return;
  const modal = document.getElementById(trigger.getAttribute('data-modal-open'));
  if (modal && typeof modal.open === 'function') {
    e.preventDefault();
    modal.open(trigger);
  }
});

/* ---------- Back button: history.back() with safe href fallback ---------- */

document.addEventListener('click', (e) => {
  const back = e.target.closest('[data-history-back]');
  if (!back) return;
  if (history.length > 1 && document.referrer) {
    try {
      if (new URL(document.referrer).origin === location.origin) {
        e.preventDefault();
        history.back();
      }
    } catch (_) { /* fall through to the href */ }
  }
});

/* ---------- data-href: a button that navigates ----------
   Crawlers count a nofollowed or robots-blocked <a href> against the
   site, so the account, login, wishlist, cart and search entries are
   buttons carrying their URL in data-href. A handler that already
   handled the click (the cart drawer toggle) calls preventDefault and
   is left alone; modifier clicks open a new tab the way a link would. */
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-href]');
  if (!btn || btn.disabled || e.defaultPrevented) return;
  const href = btn.getAttribute('data-href');
  if (!href) return;
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) {
    window.open(href, '_blank', 'noopener');
    return;
  }
  window.location.href = href;
});

/* ---------- Expose on window for non-module callers ---------- */

window.LaNuit = {
  debounce,
  throttle,
  fetchJSON,
  trapFocus,
  publishCart,
  Cart
};
